import * as React from "react";
import { showPluginToast } from "../shell/PluginToast";
import { IconChevronDown, IconChevronRight, IconCopy } from "./icons/ChatIcons";

function formatMessageTime(iso: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

async function copyToClipboard(text: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
    showPluginToast("Copied to clipboard");
  } catch {
    showPluginToast("Could not copy to clipboard");
  }
}

const SELECTED_TEXT_PREVIEW_LEN = 36;

function UserSelectedTextAccordion({ selectedText }: { selectedText: string }) {
  const trimmed = selectedText.trim();
  const [open, setOpen] = React.useState(false);
  if (!trimmed) return null;

  const needsToggle = trimmed.length > SELECTED_TEXT_PREVIEW_LEN;
  const preview = needsToggle ? `${trimmed.slice(0, SELECTED_TEXT_PREVIEW_LEN)}…` : trimmed;
  const displayText = needsToggle && !open ? preview : trimmed;

  return (
    <div className="chat-selected-accordion">
      <span className="chat-selected-accordion__label">Selected text</span>
      {needsToggle ? (
        <button
          type="button"
          className="chat-selected-accordion__toggle"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
        >
          <span className="chat-selected-accordion__chevron">
            {open ? <IconChevronDown /> : <IconChevronRight />}
          </span>
          <span className="chat-selected-accordion__text">{displayText}</span>
        </button>
      ) : (
        <span className="chat-selected-accordion__text">{displayText}</span>
      )}
    </div>
  );
}

function MessageFooter({
  align,
  createdAt,
  copyText,
}: {
  align: "left" | "right";
  createdAt: string;
  copyText: string;
}) {
  const time = formatMessageTime(createdAt);
  return (
    <div className={`chat-msg-footer chat-msg-footer--${align}`}>
      {time ? <span>{time}</span> : null}
      <button
        type="button"
        className="chat-msg-footer__copy"
        aria-label="Copy message"
        onClick={() => void copyToClipboard(copyText)}
      >
        <IconCopy size={12} />
      </button>
    </div>
  );
}

export type ChatUserBubbleProps = {
  content: string;
  selectedText: string;
  createdAt: string;
};

export const ChatUserBubble: React.FC<ChatUserBubbleProps> = ({
  content,
  selectedText,
  createdAt,
}) => (
  <div className="chat-user-row">
    <div className={`chat-user-col${selectedText.trim() ? " chat-user-col--with-selection" : ""}`}>
      <div className="chat-user-bubble">
        {content}
        <UserSelectedTextAccordion selectedText={selectedText} />
      </div>
      <MessageFooter align="right" createdAt={createdAt} copyText={content} />
    </div>
  </div>
);

export { MessageFooter, copyToClipboard, formatMessageTime };
